import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, Image, ActivityIndicator } from 'react-native';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@cubemeet/core/services/supabase';
import { Breadcrumbs } from '@cubemeet/ui';
import { resolveCityKey, getCityDisplayNameZH } from '@cubemeet/module-travel/shared/cities';

// Small, pragmatic implementation of a Guide index page with filters
// Route: /travel_post/guide?city=kyoto&type=guide

// Local list of supported cities (keep in sync with module-travel shared types)
const CITY_KEYS = ['tokyo', 'osaka', 'kyoto', 'hongkong', 'taipei', 'fuji'] as const;

// Type options — default is 'guide'; others are optional extensions
const TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: 'guide', label: '指南' },
  { value: 'itinerary', label: '行程' },
  { value: 'tips', label: '攻略' },
];

const PAGE_SIZE = 12;

const GuideIndexPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const selectedCityParam = searchParams.get('city') || '';
  const selectedTypeParam = (searchParams.get('type') || 'guide').toLowerCase();

  const selectedCityKey = useMemo(() => resolveCityKey(selectedCityParam), [selectedCityParam]);
  const selectedType = useMemo(() => TYPE_OPTIONS.find(t => t.value === selectedTypeParam)?.value || 'guide', [selectedTypeParam]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<any[]>([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  // Update page title/meta
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.title = '旅行指南 · CubeMeet';
    }
  }, []);

  const updateQuery = useCallback((cityKey: string | null, type: string) => {
    const params: Record<string, string> = {};
    if (cityKey) params.city = cityKey;
    if (type && type !== 'guide') params.type = type; // guide is the default
    setSearchParams(params, { replace: true });
  }, [setSearchParams]);

  const fetchPage = useCallback(async (pageIndex: number) => {
    setLoading(true);
    setError(null);
    try {
      let query: any = supabase
        .from('travel_posts')
        .select('id,title,summary,cover_image,content_json,created_at,status')
        .eq('status', 'published')
        .eq('content_json->>type', selectedType)
        .order('created_at', { ascending: false })
        .range(pageIndex * PAGE_SIZE, (pageIndex + 1) * PAGE_SIZE - 1);

      if (selectedCityKey) {
        const cityLabel = getCityDisplayNameZH(selectedCityKey as any);
        // Match common cases: content_json.city combined aliases string, plain city column, or general text
        const orExpr = [
          `content_json->>city.ilike.%${cityLabel}%`,
          `city.ilike.%${cityLabel}%`,
          `content_json::text.ilike.%${cityLabel}%`,
        ].join(',');
        query = query.or(orExpr);
      }

      const { data, error: qerr } = await query;
      if (qerr) {
        setError('加载失败，请稍后重试');
        setLoading(false);
        return { data: [], pageIndex };
      }

      const fetched = Array.isArray(data) ? data : [];
      setItems(prev => pageIndex === 0 ? fetched : [...prev, ...fetched]);
      setHasMore(fetched.length === PAGE_SIZE);
      setPage(pageIndex);
    } catch (e) {
      console.warn('GuideIndexPage: fetch error', e);
      setError('加载失败，请检查网络连接');
    } finally {
      setLoading(false);
    }
  }, [selectedCityKey, selectedType]);

  // Initial and on-filter change fetch
  useEffect(() => {
    fetchPage(0);
  }, [fetchPage]);

  // Handlers
  const onChangeCity = (key: string) => {
    const nextKey = key === 'all' ? null : key;
    updateQuery(nextKey, selectedType);
  };

  const onChangeType = (type: string) => {
    updateQuery(selectedCityKey, type);
  };

  const onLoadMore = () => {
    if (loading || !hasMore) return;
    fetchPage(page + 1);
  };

  const cityOptions = useMemo(() => [{ key: 'all', label: '全部城市' }, ...CITY_KEYS.map((k) => ({ key: k, label: getCityDisplayNameZH(k as any) }))], []);

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.contentContainer}>
      <Breadcrumbs
        items={[{ label: '首頁', to: '/' }, { label: '指南' }]}
        onNavigate={(to) => navigate(to)}
      />

      {/* Filters */}
      <View style={styles.filtersRow}>
        <FilterPillGroup
          label="城市"
          options={cityOptions}
          value={selectedCityKey || 'all'}
          onChange={onChangeCity}
        />
        <FilterPillGroup
          label="类型"
          options={TYPE_OPTIONS.map(o => ({ key: o.value, label: o.label }))}
          value={selectedType}
          onChange={onChangeType}
        />
      </View>

      {/* List */}
      <View style={styles.grid}>
        {items.map((post) => (
          <GuideCard key={post.id} post={post} onPress={() => navigate(`/travel-post/${post.id}`)} />
        ))}
      </View>

      {/* States */}
      {loading && page === 0 && (
        <View style={styles.centerBlock}>
          <ActivityIndicator size="small" color="#3B82F6" />
          <Text style={styles.muted}>正在加载…</Text>
        </View>
      )}
      {error && (
        <View style={styles.centerBlock}>
          <Text style={styles.errorText}>{error}</Text>
        </View>
      )}
      {!loading && items.length === 0 && !error && (
        <View style={styles.centerBlock}>
          <Text style={styles.muted}>暂无内容</Text>
        </View>
      )}

      {/* Pagination */}
      {!loading && hasMore && (
        <TouchableOpacity style={styles.loadMoreBtn} onPress={onLoadMore} activeOpacity={0.9}>
          <Text style={styles.loadMoreText}>加载更多</Text>
        </TouchableOpacity>
      )}

      {/* Bottom padding */}
      <View style={{ height: 24 }} />
    </ScrollView>
  );
};

const GuideCard: React.FC<{ post: any; onPress: () => void }> = ({ post, onPress }) => {
  return (
    <TouchableOpacity style={styles.card} onPress={onPress} activeOpacity={0.95}>
      {post.cover_image ? (
        <Image source={{ uri: post.cover_image }} style={styles.cardImage as any} />
      ) : (
        <View style={[styles.cardImage as any, { backgroundColor: '#F3F4F6' }]} />
      )}
      <View style={styles.cardBody}>
        <Text style={styles.cardTitle} numberOfLines={2}>{post.title}</Text>
        {post.summary ? (
          <Text style={styles.cardSummary} numberOfLines={2}>{post.summary}</Text>
        ) : null}
        <View style={styles.cardMetaRow}>
          {post.content_json?.city ? (
            <Text style={styles.cardMetaCity} numberOfLines={1}>📍 {String(post.content_json.city).split(',')[1] || String(post.content_json.city)}</Text>
          ) : null}
          <Text style={styles.cardMetaDate}>{new Date(post.created_at).toLocaleDateString('zh-CN')}</Text>
        </View>
      </View>
    </TouchableOpacity>
  );
};

const FilterPillGroup: React.FC<{ label?: string; value: string; options: { key: string; label: string }[]; onChange: (k: string) => void }> = ({ label, value, options, onChange }) => {
  return (
    <View style={styles.pillGroup}>
      {label ? <Text style={styles.pillLabel}>{label}</Text> : null}
      <View style={styles.pillRow}>
        {options.map(opt => (
          <TouchableOpacity
            key={opt.key}
            style={[styles.pill, value === opt.key && styles.pillActive]}
            onPress={() => onChange(opt.key)}
            activeOpacity={0.9}
          >
            <Text style={[styles.pillText, value === opt.key && styles.pillTextActive]}>{opt.label}</Text>
          </TouchableOpacity>
        ))}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#FFFFFF' },
  contentContainer: { padding: 16 },
  filtersRow: { marginBottom: 12 },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    gap: 12 as any,
  },
  card: { width: '48%', backgroundColor: '#fff', borderRadius: 12, overflow: 'hidden', borderWidth: 1, borderColor: '#E5E7EB', marginBottom: 12 },
  cardImage: { width: '100%', aspectRatio: 16/10, backgroundColor: '#F9FAFB' },
  cardBody: { padding: 10 },
  cardTitle: { fontSize: 14, fontWeight: '700', color: '#111827' },
  cardSummary: { marginTop: 6, fontSize: 12, color: '#6B7280', lineHeight: 18 },
  cardMetaRow: { marginTop: 8, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  cardMetaCity: { fontSize: 12, color: '#10B981', flexShrink: 1, paddingRight: 8 },
  cardMetaDate: { fontSize: 12, color: '#9CA3AF' },

  centerBlock: { alignItems: 'center', paddingVertical: 16 },
  muted: { fontSize: 12, color: '#6B7280', marginTop: 6 },
  errorText: { fontSize: 13, color: '#EF4444' },
  loadMoreBtn: { marginTop: 4, alignSelf: 'center', paddingHorizontal: 16, paddingVertical: 10, borderRadius: 999, backgroundColor: '#111827' },
  loadMoreText: { color: '#fff', fontWeight: '600', fontSize: 13 },

  pillGroup: { marginBottom: 8 },
  pillLabel: { fontSize: 12, color: '#6B7280', marginBottom: 6 },
  pillRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 as any },
  pill: { paddingHorizontal: 12, paddingVertical: 8, borderRadius: 999, borderWidth: 1, borderColor: '#E5E7EB', backgroundColor: '#FFFFFF' },
  pillActive: { backgroundColor: '#111827', borderColor: '#111827' },
  pillText: { fontSize: 12, color: '#374151' },
  pillTextActive: { color: '#FFFFFF', fontWeight: '700' },
});

export default GuideIndexPage;
